<template>
  <section class="m-4 md:mx-8 md:mt-12">
    <div class="flex justify-end mb-2">
      <ButtonAction @action="openEventModal(null)" color="gray">
        {{ $t('lang.msg.addEvent') }}
      </ButtonAction>
    </div>
    <div class="px-4 md:px-8 py-2 text-2xl font-bold bg-primario text-gray-200 tracking-wide rounded-t">
      <h2>{{ $t("lang.msg.calendar") }}</h2>
    </div>
    <vue-cal
      style="height: 675px"
      :selected-date="today"
      :disable-views="['years']"
      :events="calendarEvents"
      :locale="language"
      :time-from="0"
      :time-to="24 * 60"
      :time-step="60"
      :time-cell-height="50"
      :on-event-click="onEventClick"
      :min-cell-width="145"
      :min-event-width="80"
      :overlaps-per-time-step="true"
      :activeView="activeView"
      @view-change="fetchData($event.startDate)"
    />

    <TheModal :open="isModalOpen" @close="isModalOpen = false" :title="currentEvent._eid == undefined ? $t('lang.msg.createEvent') : $t('lang.msg.updateEvent')">
      <template slot="body">
        <div class="flex">
          <div class="w-full pt-3 space-y-4">
            <div class="flex space-x-2">
              <InputWrapper :label="$t('lang.msg.eventDate')" block content="input">
                <v-date-picker
                  mode="single"
                  v-model="currentEvent.start"
                  title-position="left"
                  :input-props="{
                    class:
                      'datepicker w-full hover:border-blue-500',
                    placeholder: $t('lang.msg.eventDate')
                  }"
                />
              </InputWrapper>
              <InputWrapper :label="$t('lang.msg.startTime')" block content="input">
                <InputText type="time" v-model="currentEvent.startTime" :name="$t('lang.msg.startTime')" />
              </InputWrapper>
            </div>
            <InputWrapper :label="$t('lang.msg.duration')" block content="input">
              <InputText type="time" v-model="currentEvent.duration" :name="$t('lang.msg.duration')" />
            </InputWrapper>
            <InputWrapper :label="$t('lang.msg.title')" block content="input">
              <InputText v-model="currentEvent.title" :name="$t('lang.msg.title')" />
            </InputWrapper>
            <InputWrapper :label="$t('lang.msg.description')" block content="input">
              <textarea
                rows="2"
                class="form-textarea w-full mt-1"
                v-model="currentEvent.description"
                :placeholder="$t('lang.msg.description')"
              />
            </InputWrapper>
            <div class="grid grid-rows-2 grid-cols-5 gap-4">
              <SelectableColor
                v-for="(color, index) in eventColors"
                :key="`color_${index}`"
                :color="color"
                v-model="currentEvent.class"
                @on-check="selectColor"
              />
            </div>
          </div>
        </div>
      </template>
      <template #action>
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 hover:bg-green-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
            @click="addOrEditEvent"
            @keyup.enter="addOrEditEvent"
          >{{ currentEvent._eid == undefined ? $t('lang.msg.add') : $t('lang.msg.update') }}</button>
        </span>
      </template>
    </TheModal>
  </section>
</template>

<script>
import { mapState } from "vuex";
import TheModal from "@/components/shared/TheModal";
import SelectableColor from "@/components/core-ui/SelectableColor";
import CalendarMixin from "../components/mixins/CalendarMixin";

import VueCal from "vue-cal";
import "vue-cal/dist/vuecal.css";
import "vue-cal/dist/i18n/es.js";

export default {
  name: "Calendar",
  components: {
    "vue-cal": VueCal,
    TheModal,
    SelectableColor
  },
  mixins: [CalendarMixin],
  data() {
    return {
      today: new Date(),
      attrs: [],
      calendarEvents: [],
      isModalOpen: false,
      currentEvent: {},
      activeView:'week',
      eventColors: [
        { color: "red", name: this.$t("lang.msg.nationalHolidays") },
        { color: "orange", name: this.$t("lang.msg.internationalHolidays") },
        { color: "blue", name: this.$t("lang.msg.generalMeetings") },
        { color: "yellow", name: this.$t("lang.msg.sampleDelivery2") },
        { color: "green", name: this.$t("lang.msg.sampleDelivery") },
        { color: "pink", name: this.$t("lang.msg.departmentMeetings") },
        { color: "lila", name: this.$t("lang.msg.factoryHolidays") },
        { color: "purple", name: this.$t("lang.msg.clientHolidays") },
        { color: "gray", name: this.$t("lang.msg.productionDelivery") }
      ]
    };
  },
  computed: {
    ...mapState("language", ["language"])
  },
  created() {
    this.fetchData();
      if (this.$route.params.today != undefined) {
          this.today = new Date(this.$route.params.today)
          this.activeView = 'day'
      }
  },
  methods: {
    fetchData(date) {
      // Date will be undefined at the first view.
      if (date === undefined) date = new Date();

      this.$axios
        .post(`api/GeneralTasks/GetCalendar`, {
          idUser: this.user.id,
          mes: parseInt(this.$moment(date).format("M")),
          año: parseInt(this.$moment(date).format("YYYY"))
        })
        .then((response) => {
          response.data.forEach((el) => {
            // Format both 'start' and 'end' date
            el.start = this.$moment(el.start).format("YYYY-MM-DD HH:mm:ss");
            el.end = this.$moment(el.end).format("YYYY-MM-DD HH:mm:ss");

            el.duration = this.formatMinutesToTime(el.duration);
          });

          // Set the response to the object after it has been formatted
          this.calendarEvents = response.data;
        })
        .catch(() => {
          this.showFriendlyError();
        });
    },
    addOrEditEvent() {
      // Add the time when the event starts.
      let start = this.$moment(this.currentEvent.start).format("YYYY-MM-DD");
      start += " " + this.currentEvent.startTime;

      // Calculamos la duración del evento según lo que recibos del campo duración (Formato HH:MM)
      let durationSplit = this.currentEvent.duration.split(":");
      let durationInMinutes = parseInt(durationSplit[0]) * 60 + parseInt(durationSplit[1]);

      // Format the newEvent with the needed property names.
      let newEvent = {
        idUser: this.user.id,
        idGeneralTask: this.currentEvent.idGeneralTask || 0,
        class: this.currentEvent.class,
        title: this.currentEvent.title,
        description: this.currentEvent.description,
        duration: durationInMinutes,
        startTime: this.currentEvent.startTime,
        start: start,
        end: this.$moment(start).add(durationInMinutes, "minutes").format("YYYY-MM-DD HH:mm")
      };

      // Copy the previous object and add only the formatted dates.
      let newEventWithDates = { ...newEvent };
      newEventWithDates["start"] = this.$moment(start).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
      newEventWithDates["end"] = this.$moment(newEvent.end).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

      // Find the event by its id. (¿idNotification?)
      let eventIndex = this.calendarEvents.findIndex((element) => {
        return element.idGeneralTask == this.currentEvent.idGeneralTask;
      });

      // Add or update the event
      if (eventIndex != -1) {
        this.$axios
          .post("api/GeneralTasks/Update", newEventWithDates)
          .then(() => {
            // Format the duration from minutes to HH:MM.
            newEvent.duration = this.formatMinutesToTime(newEvent.duration);

            Object.assign(this.calendarEvents[eventIndex], newEvent);
            this.openAlert("success", "Evento actualizado", null);
          });
      } else {
        this.$axios.post("api/GeneralTasks/Add", newEventWithDates).then(() => {
          // Update all the events.
          this.fetchData();

          this.openAlert("success", "Evento añadido", null);
        });
      }

      // Close modal
      this.isModalOpen = false;
    },
    formatMinutesToTime(minutes) {
      let durationHours = Math.floor(minutes / 60);
      if (durationHours < 10) {
        durationHours = '0' + durationHours;
      }

      let durationMinutes =  minutes % 60;
      if (durationMinutes < 10) {
        durationMinutes = '0' + durationMinutes;
      }

      return durationHours + ":" + durationMinutes;
    },
    onEventClick(event, e) {
      this.openEventModal(event);

      e.stopPropagation();
    },
    openEventModal(item) {
      this.currentEvent = item || {};
      this.isModalOpen = true;
    },
    selectColor(color) {
      this.$set(this.currentEvent, "class", color);
    }
  }
};
</script>

<style>
.vuecal__menu,
.vuecal__cell-events-count {
  background-color: #939393;
}
.vuecal__menu button {
  border-bottom-color: #fff;
  color: #fff;
}
.vuecal__menu button.active {
  background-color: rgba(255, 255, 255, 0.15);
}
.vuecal__title-bar {
  background-color: #edf2f7;
}
.vuecal__flex .vuecal__cell-content {
  cursor: pointer;
}
.vuecal:not(.vuecal--day-view) .vuecal__cell.selected {
  background-color: #f7fafc;
}
.vuecal__cell.selected:before {
  border-color: #1a202c;
}
.vuecal__event-title {
  padding: 0 2px;
  font-weight: 500;
  font-size: 12px;
}
.vuecal__event-time {
  font-size: 11px;
}
.vuecal__event {
  border-top: 4px solid #2d3748;
  background-color: #939393;
  color: #474849;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100px;
}
.vuecal__event:hover {
  background-color: #474849;
  color: #f7fafc;
}
.vuecal__event.black {
  background-color: black;
  border-top: 4px solid black;
  color: white;
}
.vuecal__event.black:hover {
  background-color: black;
}
.vuecal__event.gray {
  background-color: #718096;
  border-top: 4px solid #4a5568;
  color: white;
}
.vuecal__event.gray:hover {
  background-color: #4a5568;
}
.vuecal__event.red {
  background-color: #e53e3e;
  border-top: 4px solid #c53030;
  color: white;
}
.vuecal__event.red:hover {
  background-color: #c53030;
}
.vuecal__event.orange {
  background-color: #dd6b20;
  border-top: 4px solid #c05621;
  color: white;
}
.vuecal__event.orange:hover {
  background-color: #c05621;
}
.vuecal__event.yellow {
  background-color: #d69e2e;
  border-top: 4px solid #b7791f;
  color: white;
}
.vuecal__event.yellow:hover {
  background-color: #b7791f;
}
.vuecal__event.green {
  background-color: #38a169;
  border-top: 4px solid #2f855a;
  color: white;
}
.vuecal__event.green:hover {
  background-color: #2f855a;
}
.vuecal__event.blue {
  background-color: #3182ce;
  border-top: 4px solid #2b6cb0;
  color: white;
}
.vuecal__event.blue:hover {
  background-color: #2b6cb0;
}
.vuecal__event.lila {
  background-color: #d891ef;
  border-top: 4px solid #c283d7;
  color: white;
}
.vuecal__event.lila:hover {
  background-color: #c283d7;
}
.vuecal__event.purple {
  background-color: #805ad5;
  border-top: 4px solid #6b46c1;
  color: white;
}
.vuecal__event.purple:hover {
  background-color: #6b46c1;
}
.vuecal__event.pink {
  background-color: #d53f8c;

  border-top: 4px solid #b83280;
  color: white;
}
.vuecal__event.pink:hover {
  background-color: #b83280;
}
/* DAY EVENT */
/* MONTHLY EVENT COUNTER */
.vuecal__cell-events-count {
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  font-size: 12px;
}
/* MONTHLY EVENT COUNTER */
.vuecal__time-column .vuecal__time-cell {
  padding-right: 5px !important;
}
.vuecal__time-column .vuecal__time-cell:first-child {
  border-top: 1px solid #ddd;
}
.vuecal__title-bar {
  border-left: 1px solid #ddd;
  border-right: 1px solid #ddd;
}
</style>
